import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { GridRowId } from "@mui/x-data-grid-pro";
import { ContainedButton, LoadingIndicator, TextInput } from "shared/components";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  Wrapper,
  Title,
  ToolboxWrapper,
  SearchWrapper,
  ButtonsWrapper,
  IconButton
} from "../DashboardTableStyles";
import { DataTable, DataTableColumn } from "../DataTable";
import { useSubscriptionsData, useSubscriptionsExport } from "../../hooks";
import { SubscriptionWithStatus, SubscriptionStatus } from "../../types";
import { SubscriptionReportModal } from "../SubscriptionReportModal";

const columns: DataTableColumn[] = [
  {
    name: "timeOfSubscription",
    translationKey: "dashboardModule.subscriptionTable.table.columns.timeOfSubscription"
  },
  {
    name: "templateType",
    translationKey: "dashboardModule.subscriptionTable.table.columns.templateType"
  },
  {
    name: "language",
    translationKey: "dashboardModule.subscriptionTable.table.columns.language"
  },
  {
    name: "email",
    translationKey: "dashboardModule.subscriptionTable.table.columns.email"
  },
  {
    name: "firstName",
    translationKey: "dashboardModule.subscriptionTable.table.columns.firstName"
  },
  {
    name: "lastName",
    translationKey: "dashboardModule.subscriptionTable.table.columns.lastName"
  },
  {
    name: "gender",
    translationKey: "dashboardModule.subscriptionTable.table.columns.gender"
  },
  {
    name: "birthdate",
    translationKey: "dashboardModule.subscriptionTable.table.columns.birthdate"
  }
];

export const SubscriptionsTable = () => {
  const { t } = useTranslation();

  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);

  const [subscriptionReportModalOpen, setSubscriptionReportModalOpen] = useState(false);

  const handleSelectRows = useCallback((rows: GridRowId[]) => {
    setSelectedRows(rows);
  }, []);

  const handleOpenSubscriptionReportModal = useCallback(() => {
    setSubscriptionReportModalOpen(true);
  }, []);

  const handleCloseSubscriptionReportModal = useCallback(() => {
    setSubscriptionReportModalOpen(false);
  }, []);

  const handleSelectableRowCheck = useCallback(({ status }: SubscriptionWithStatus) => {
    return status === SubscriptionStatus.DEFAULT;
  }, []);

  const handleRowClassName = useCallback(({ status }: SubscriptionWithStatus) => {
    return `grid-row-${status}`;
  }, []);

  const handleReportSubmit = useCallback(() => {
    setSelectedRows([]);
    setSubscriptionReportModalOpen(false);
  }, []);

  const {
    data,
    totalRows,
    loading,
    fetching,
    searchValue,
    requestQueryKey,
    handleSearchChange,
    handleSortingChange,
    handlePaginationChange,
    sort,
    pagination
  } = useSubscriptionsData();

  const { executeSubscriptionsExport, loading: exportLoading } = useSubscriptionsExport();

  const selectedItems = useMemo(() => {
    return data.filter((item) => selectedRows.includes(item.id));
  }, [selectedRows, data]);

  const reportButtonDisabled =
    loading || fetching || data.length === 0 || selectedRows.length === 0;

  const searchDisabled = !searchValue && (loading || data.length === 0);

  const exportButtonDisabled = loading || data.length === 0;

  const tableLoading = loading || fetching;

  return (
    <Wrapper>
      <Title>{t("dashboardModule.subscriptionTable.title")}</Title>
      <ToolboxWrapper>
        <SearchWrapper>
          <TextInput
            label={t("dashboardModule.subscriptionTable.search.label")}
            placeholder={t("dashboardModule.subscriptionTable.search.placeholder")}
            infoText={t("dashboardModule.subscriptionTable.search.hint")}
            value={searchValue}
            onChange={handleSearchChange}
            marginBottom={0}
            disabled={searchDisabled}
          />
        </SearchWrapper>
        <ButtonsWrapper>
          <ContainedButton
            disabled={reportButtonDisabled}
            onClick={handleOpenSubscriptionReportModal}
          >
            {t("dashboardModule.subscriptionTable.reportButton")}
          </ContainedButton>
          <IconButton disabled={exportButtonDisabled} onClick={executeSubscriptionsExport}>
            {exportLoading ? <LoadingIndicator size={24} /> : <FileDownloadIcon />}
          </IconButton>
        </ButtonsWrapper>
      </ToolboxWrapper>
      <DataTable
        tableLocales={{
          noRowsLocalizationKey: "dashboardModule.subscriptionTable.table.emptyState",
          pageSizeOptionsLabel: "dashboardModule.subscriptionTable.table.pageSizeText"
        }}
        columns={columns}
        data={data}
        loading={tableLoading}
        selectedRows={selectedRows}
        sortingData={sort}
        paginationData={pagination}
        totalRows={totalRows}
        onSelectRows={handleSelectRows}
        onSortingChange={handleSortingChange}
        onPaginationChange={handlePaginationChange}
        isRowSelectable={handleSelectableRowCheck}
        getRowClassName={handleRowClassName}
      />
      <SubscriptionReportModal
        dataQueryKey={requestQueryKey}
        open={subscriptionReportModalOpen}
        data={selectedItems}
        onClose={handleCloseSubscriptionReportModal}
        onSubmit={handleReportSubmit}
      />
    </Wrapper>
  );
};
